import type {
  AnalyticMaterial,
  BuildingAnalyticEntity,
  BuildingAnalyticEntityStyle,
  BuildingAnalyticExtrusion,
  BuildingAnalyticFace,
  BuildingAnalyticLineSegment,
  BuildingAnalyticPolyLine,
} from "building-types/buildingAnalyticEntityTypes";
import {
  TAG_CONDITION_FORCE_DOUBLE_SIDED,
  type BuildingGeomTag,
} from "building-types/buildingGeomTag/buildingGeomTagTypes";
import type { BuildingAnalyticEntityType } from "building-types/entities/buildingEntityTypeTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { RWTable } from "common/data/table";
import type { MaterialSrc } from "common/materials/materialSrc";
import uid from "common/utils/uid";
import type { Face, Extrusion, BRep } from "geom/brep";
import type { Line3Segment } from "geom/line3";
import { type PolyLine3, polyLine3EdgeCount, polyLine3Edge } from "geom/polyLine3";
import type { PolyLineEdgeType } from "geom/polyLineTypes";
import type { UVTransformation } from "geom/uv";
import type { Vec2 } from "geom/vec2";
import {
  buildingAnalyticLineSegmentNew,
  buildingAnalyticPolyLineNew,
  buildingAnalyticFaceNew,
  buildingAnalyticExtrusionNew,
  buildingAnalyticBRepNew,
} from "../elements/buildingAnalyticEntity";

export function addAnalyticBRep(
  entityType: BuildingAnalyticEntityType,
  entities: RWTable<BuildingAnalyticEntity>,
  name: string,
  parents: DocEntityHandle[],
  brep: BRep,
  materialSrc: MaterialSrc | undefined,
  materialTransformations: (UVTransformation | undefined)[] | undefined,
  projectedOnPlane: Face | undefined,
  tags: BuildingGeomTag[],
  style?: BuildingAnalyticEntityStyle,
  id?: string,
) {
  id = id ?? uid();
  const analyticEntity = buildingAnalyticBRepNew(
    entityType,
    name,
    parents,
    brep,
    materialSrc,
    materialTransformations,
    projectedOnPlane,
    id,
    tags,
    style,
  );
  entities[id] = analyticEntity;
  return analyticEntity;
}

export function addPolyLine3s(
  entityType: BuildingAnalyticEntityType,
  entities: RWTable<BuildingAnalyticEntity>,
  name: string,
  parents: DocEntityHandle[],
  polyLine3s: PolyLine3[],
  tags: BuildingGeomTag[],
  style?: BuildingAnalyticEntityStyle,
) {
  for (let i = 0; i < polyLine3s.length; i++) {
    const polyLine3 = polyLine3s[i];
    addPolyLine3Edges(entityType, entities, name, parents, polyLine3, tags, style);
  }
}

export function addPolyLine3Edges(
  entityType: BuildingAnalyticEntityType,
  entities: RWTable<BuildingAnalyticEntity>,
  name: string,
  parents: DocEntityHandle[],
  polyLine3: PolyLine3,
  tags: BuildingGeomTag[],
  style?: BuildingAnalyticEntityStyle,
  id?: string,
) {
  const edges = polyLine3EdgeCount(polyLine3);
  for (let i = 0; i < edges; i++) {
    const lineSegment = polyLine3Edge(polyLine3, i) as unknown as Line3Segment;
    addAnalyticLineSegment(entityType, entities, name, parents, lineSegment, tags, style, id);
  }
}

export function addAnalyticLineSegment(
  entityType: BuildingAnalyticEntityType,
  entities: RWTable<BuildingAnalyticEntity>,
  name: string,
  parents: DocEntityHandle[],
  lineSegment: Line3Segment,
  tags: BuildingGeomTag[],
  style?: BuildingAnalyticEntityStyle,
  id?: string,
): BuildingAnalyticLineSegment {
  id = id ?? uid();
  const analyticEntity = buildingAnalyticLineSegmentNew(entityType, name, parents, lineSegment, id, tags, style);
  entities[id] = analyticEntity;
  return analyticEntity;
}

export function addAnalyticPolyLine(
  entityType: BuildingAnalyticEntityType,
  entities: RWTable<BuildingAnalyticEntity>,
  name: string,
  parents: DocEntityHandle[],
  polyLine: PolyLine3,
  tags: BuildingGeomTag[],
  style?: BuildingAnalyticEntityStyle,
  id?: string,
): BuildingAnalyticPolyLine {
  id = id ?? uid();
  const analyticEntity = buildingAnalyticPolyLineNew(entityType, name, parents, polyLine, id, tags, style);
  entities[id] = analyticEntity;
  return analyticEntity;
}

export function addFaces(
  entityType: BuildingAnalyticEntityType,
  entities: RWTable<BuildingAnalyticEntity>,
  name: string,
  parents: DocEntityHandle[],
  faces: Face[],
  material: AnalyticMaterial = {},
  tags: BuildingGeomTag[],
  style?: BuildingAnalyticEntityStyle,
): BuildingAnalyticFace[] {
  const analyticFaces: BuildingAnalyticFace[] = [];
  for (let i = 0; i < faces.length; i++) {
    const face = faces[i];
    const analyticFace = addAnalyticFace(entityType, entities, name, parents, face, material, tags, style);
    if (analyticFace) {
      analyticFaces.push(analyticFace);
    }
  }
  return analyticFaces;
}

export function addAnalyticFace(
  entityType: BuildingAnalyticEntityType,
  entities: RWTable<BuildingAnalyticEntity>,
  name: string,
  parents: DocEntityHandle[],
  face: Face,
  { materialSrc, materialTransformation }: AnalyticMaterial = {},
  tags: BuildingGeomTag[],
  style?: BuildingAnalyticEntityStyle,
  loopEdgeTypes?: readonly (readonly PolyLineEdgeType[])[],
): BuildingAnalyticFace | undefined {
  if (face.tags?.includes(TAG_CONDITION_FORCE_DOUBLE_SIDED)) {
    // ignore faces that are created purely to force double-sided rendering
    return undefined;
  }

  const id = uid();
  const analyticEntity = buildingAnalyticFaceNew(
    entityType,
    name,
    parents,
    face,
    materialSrc,
    materialTransformation,
    id,
    tags,
    style,
    loopEdgeTypes,
  );
  entities[id] = analyticEntity;
  return analyticEntity;
}

export function addAnalyticExtrusion(
  entityType: BuildingAnalyticEntityType,
  entities: RWTable<BuildingAnalyticEntity>,
  name: string,
  parents: DocEntityHandle[],
  extrusion: Extrusion,
  materialSrc: MaterialSrc | undefined,
  tags: BuildingGeomTag[],
  style?: BuildingAnalyticEntityStyle,
  type?: string,
  lenDir?: Vec2,
  useProfileLocalDimensions?: boolean,
): BuildingAnalyticExtrusion {
  const id = uid();
  const analyticEnity = buildingAnalyticExtrusionNew(
    entityType,
    name,
    parents,
    extrusion,
    materialSrc,
    id,
    tags,
    style,
    type,
    lenDir,
    useProfileLocalDimensions,
  );
  entities[id] = analyticEnity;
  return analyticEnity;
}
